/** Matches dbo.PortalRole.Name and API PortalConstants.LegalResourcesAdminRoleName. */
export const LEGAL_RESOURCES_ADMIN_ROLE = 'LegalResourcesAdmin';

export type PortalLoginKey = 'client' | 'attorney' | (string & {});

export function normalizePortalLoginKey(portalKey: string): string {
  return portalKey.trim().toLowerCase();
}

export function isLegalResourcesAdmin(roles: readonly string[]): boolean {
  return roles.some(role => role === LEGAL_RESOURCES_ADMIN_ROLE);
}

/** Client portal login: LR Admin gets every active master client from the API. */
export function shouldHydrateAllClientsOnLogin(portalKey: PortalLoginKey, roles: readonly string[]): boolean {
  return normalizePortalLoginKey(portalKey) === 'client' && isLegalResourcesAdmin(roles);
}

/** Attorney portal login: LR Admin gets every selectable firm group from the API. */
export function shouldHydrateAllFirmsOnLogin(portalKey: PortalLoginKey, roles: readonly string[]): boolean {
  return normalizePortalLoginKey(portalKey) === 'attorney' && isLegalResourcesAdmin(roles);
}

/** User must pick a client (picker page or header) before working in the client portal. */
export function needsClientPortalSelection(roles: readonly string[], clientCount: number): boolean {
  return isLegalResourcesAdmin(roles) || clientCount > 1;
}

/** User must pick a firm before working in the attorney portal. */
export function needsAttorneyFirmSelection(roles: readonly string[], firmGroupCount: number): boolean {
  return isLegalResourcesAdmin(roles) || firmGroupCount > 1;
}

/**
 * Login callback: empty permission payload is allowed for LR Admin (API hydrates clients/firms separately).
 */
export function allowsEmptyLoginScopeForLegalResourcesAdmin(
  portalKey: PortalLoginKey,
  roles: readonly string[],
  hasPermissionRows: boolean,
  hasClients: boolean,
  hasFirms: boolean
): boolean {
  if (hasPermissionRows || hasClients || hasFirms) {
    return false;
  }
  if (!isLegalResourcesAdmin(roles)) {
    return false;
  }
  const key = normalizePortalLoginKey(portalKey);
  return key === 'client' || key === 'attorney';
}
